import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui-v2';
import { useNavbarOffset } from '../ui-v2.5/overlayBehavior';
import { ESTADO_DESDE_PROCESO, rutaFichaConVuelta } from './rutasProceso';
import './pantallaProceso.css';

/**
 * PantallaProceso — el marco de la pantalla de un alta o de una baja, con su propia ruta.
 *
 * ── LO QUE ERA ANTES ───────────────────────────────────────────────────────────────────────────
 *
 * El alta y la baja se abrían en un `Drawer` de 620 px encima del listado. Tenía URL de entrada
 * (`?alta=`, `?baja=`), pero la pantalla la BORRABA al abrir el panel: recargar lo cerraba, el
 * botón «atrás» salía del listado y no había enlace a lo que se estaba viendo. Ver
 * `FASE_PANTALLA_DE_ALTA_Y_BAJA.md`, §6.
 *
 * ── LA PANTALLA ────────────────────────────────────────────────────────────────────────────────
 *
 * Todo el ancho, la cabecera, y debajo una BARRA DE ACCIONES FIJA al bajar, con una franja fina
 * encima que aparece cuando la cabecera sale de la vista (nombre, número y estado: se sabe de quién
 * es sin volver arriba). La rejilla del contenido la pone cada pantalla, por el ancho de ESTE
 * contenedor (`.proc`, ver `pantallaProceso.css`), no por el de la ventana.
 *
 * Hubo un segundo modo mientras duró la fase —el drawer en página, sin barra, con pie fijo— para
 * el alta hasta que la pieza 4 la rediseñó. Se retiró con ella.
 *
 * ── «VOLVER» Y «DATOS DE LA PERSONA» ───────────────────────────────────────────────────────────
 *
 * «Volver» vuelve a donde estabas si llegaste navegando dentro de la intranet (el embudo con sus
 * filtros, o la ficha), y al listado si entraste directo por la URL. `location.key === 'default'`
 * es la marca de react-router de «primera entrada, no hay historial propio».
 *
 * Los datos de la persona (nombre, DNI…) se corrigen en SU ficha, no aquí: no se duplica el
 * formulario. El botón lleva a la ficha en Personal con `?volver=` esta ruta, y con la marca
 * `ESTADO_DESDE_PROCESO` para que la vuelta sea retroceder en el historial. Al volver, esta
 * pantalla se monta de nuevo y pide el detalle otra vez: el nombre corregido sale sin recargar.
 * `persona` es `{ ambito, id }` del titular, o `null` si el proceso no tiene titular.
 */
export default function PantallaProceso({
  rutaListado, persona = null, header = null, children, barra = null, franjaFina = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const volver = () => (location.key !== 'default' ? navigate(-1) : navigate(rutaListado));
  const aLaFicha = () => navigate(
    rutaFichaConVuelta(persona.ambito, persona.id, location.pathname),
    { state: ESTADO_DESDE_PROCESO },
  );

  const botonesNavegacion = (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="ghost" size="sm" onClick={volver}>
        <ArrowLeft />Volver
      </Button>
      {/* «Datos de la persona» va en la barra de acciones, con las demás: la pantalla la recibe
          como función para darle `aLaFicha`. */}
    </div>
  );

  return (
    <PantallaAncha
      botonesNavegacion={botonesNavegacion}
      header={header}
      barra={typeof barra === 'function' ? barra({ aLaFicha: persona ? aLaFicha : null }) : barra}
      franjaFina={franjaFina}
    >
      {children}
    </PantallaAncha>
  );
}

/* El modo nuevo. La barra se pega DEBAJO del NavBar de la intranet, que es fijo: su alto se mide
 * (`useNavbarOffset`, 0 en móvil), no se supone. Y la franja fina aparece cuando la cabecera deja
 * de verse, medido con un IntersectionObserver que descuenta ese mismo alto. */
function PantallaAncha({ botonesNavegacion, header, barra, franjaFina, children }) {
  const alto = useNavbarOffset();
  const cabeceraRef = useRef(null);
  const [cabeceraFuera, setCabeceraFuera] = useState(false);

  useEffect(() => {
    const nodo = cabeceraRef.current;
    if (!nodo || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver(
      ([e]) => setCabeceraFuera(!e.isIntersecting),
      { rootMargin: `-${Math.round(alto)}px 0px 0px 0px` },
    );
    obs.observe(nodo);
    return () => obs.disconnect();
  }, [alto]);

  return (
    <div className="proc mx-auto max-w-[1760px] px-4 pb-10 pt-4">
      {botonesNavegacion}
      <div ref={cabeceraRef} className="mt-2 overflow-clip rounded-t-v25-lg">{header}</div>
      <div className="sticky z-20 -mx-4 px-4" style={{ top: alto }}>
        {cabeceraFuera && franjaFina}
        {barra}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}
